﻿import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

export default function CadastroPage() {
  const navigate = useNavigate();
  const { registerUser } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmedName = formData.name.trim();
    const trimmedEmail = formData.email.trim().toLowerCase();

    if (!trimmedName || !trimmedEmail || !formData.password) {
      setError('Todos os campos são obrigatórios.');
      return;
    }

    if (trimmedName.length < 3) {
      setError('O nome deve ter pelo menos 3 caracteres.');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError('Informe um e-mail válido.');
      return;
    }


    if (formData.password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres.');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('As senhas não conferem.');
      return;
    }

    setIsLoading(true);

    const result = await registerUser({
      name: trimmedName,
      email: trimmedEmail,
      password: formData.password,
    });

    setIsLoading(false);

    if (!result.success) {
      setError(result.error || 'Erro ao criar conta. Tente novamente.');
      return;
    }

    setSuccess(true);

    setTimeout(() => {
      navigate('/login');
    }, 2000);
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-portal-gray-50 px-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-white rounded-xl border border-portal-gray-200 p-8 shadow-sm">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-check-line text-3xl text-green-600"></i>
            </div>
            <h2 className="text-xl font-bold text-portal-gray-900 mb-2">
              Cadastro realizado!
            </h2>
            <p className="text-sm text-portal-gray-500 mb-6">
              Sua conta foi criada com sucesso. Você será redirecionado para o login em instantes.
            </p>
            <Link
              to="/login"
              className="inline-block text-sm font-medium text-portal-purple hover:text-portal-purple-dark transition-colors"
            >
              Ir para o login agora
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-portal-gray-50 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src="https://storage.readdy-site.link/project_files/9b8b8372-d6d7-49e0-8dfa-df331b2966eb/141b6707-ca69-4854-a349-733fc3f8fcad_compressed_Logo.webp"
            alt="Zschimmer & Schwarz Brasil"
            className="h-16 w-auto object-contain mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-portal-gray-900">
            Criar Conta
          </h1>
          <p className="text-sm text-portal-gray-500 mt-1">
            Portal do Colaborador - Segurança do Trabalho
          </p>
        </div>

        <div className="bg-white rounded-xl border border-portal-gray-200 p-8 shadow-sm">
          <h2 className="text-lg font-semibold text-portal-gray-800 mb-6">
            Preencha seus dados
          </h2>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
              <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="name" className="block text-sm font-medium text-portal-gray-700 mb-1.5">
                Nome completo
              </label>
              <input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="Digite seu nome completo"
                className="w-full px-3.5 py-2.5 border border-portal-gray-300 rounded-lg text-sm text-portal-gray-800 placeholder-portal-gray-400 focus:outline-none focus:ring-2 focus:ring-portal-purple/20 focus:border-portal-purple transition-colors"
                autoComplete="name"
              />
            </div>

            <div className="mb-4">
              <label htmlFor="email" className="block text-sm font-medium text-portal-gray-700 mb-1.5">
                E-mail
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Digite seu e-mail"
                className="w-full px-3.5 py-2.5 border border-portal-gray-300 rounded-lg text-sm text-portal-gray-800 placeholder-portal-gray-400 focus:outline-none focus:ring-2 focus:ring-portal-purple/20 focus:border-portal-purple transition-colors"
                autoComplete="email"
              />
            </div>

            <div className="mb-4">
              <label htmlFor="password" className="block text-sm font-medium text-portal-gray-700 mb-1.5">
                Senha
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ?'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Mínimo 6 caracteres"
                  className="w-full px-3.5 py-2.5 border border-portal-gray-300 rounded-lg text-sm text-portal-gray-800 placeholder-portal-gray-400 focus:outline-none focus:ring-2 focus:ring-portal-purple/20 focus:border-portal-purple transition-colors pr-10"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-portal-gray-400 hover:text-portal-gray-600 cursor-pointer"
                  tabIndex={-1}
                >
                  <i className={`${showPassword ?'ri-eye-off-line' : 'ri-eye-line'} text-sm`}></i>
                </button>
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-portal-gray-700 mb-1.5">
                Confirmar senha
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ?'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Repita a senha"
                  className="w-full px-3.5 py-2.5 border border-portal-gray-300 rounded-lg text-sm text-portal-gray-800 placeholder-portal-gray-400 focus:outline-none focus:ring-2 focus:ring-portal-purple/20 focus:border-portal-purple transition-colors pr-10"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-portal-gray-400 hover:text-portal-gray-600 cursor-pointer"
                  tabIndex={-1}
                >
                  <i className={`${showConfirmPassword ?'ri-eye-off-line' : 'ri-eye-line'} text-sm`}></i>
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-portal-purple hover:bg-portal-purple-dark text-white font-medium py-2.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {isLoading ?(
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                  Criando conta...
                </span>
              ) : (
                'Criar conta'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-portal-gray-500">
              Já tem uma conta?{' '}
              <Link
                to="/login"
                className="text-portal-purple hover:text-portal-purple-dark font-medium transition-colors"
              >
                Fazer login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

